import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Typography,
  CircularProgress,
  Box,
  Tabs,
  Tab,
} from "@mui/material";
import { GetBottleOrderSchedules } from "../../../services/BottleOrdersService";

const fmt = (val) =>
  val ? new Date(val).toLocaleDateString("en-US") : "";

const BottleOrderSchedulesDialog = ({ open, onClose, accountNumber }) => {
  const [schedules, setSchedules] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState(0);

  useEffect(() => {
    if (open && accountNumber) {
      loadSchedules();
    }
  }, [open, accountNumber]);

  const loadSchedules = async () => {
    try {
      setLoading(true);
      const res = await GetBottleOrderSchedules(accountNumber);
      setSchedules(res.results || null);
    } catch {
      setSchedules(null);
    } finally {
      setLoading(false);
    }
  };

  const tcr = schedules?.tcrSchedules || [];
  const nonTcrGroup = schedules?.nonTcrGroupSchedules || [];
  const nonTcrIndividual = schedules?.nonTcrIndividualSchedules || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Bottle Order Schedules — Account {accountNumber}</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: "divider", px: 2 }}>
              <Tab label={`TCR Schedules (${tcr.length})`} />
              <Tab label={`NonTCR Group (${nonTcrGroup.length})`} />
              <Tab label={`NonTCR Individual (${nonTcrIndividual.length})`} />
            </Tabs>

            <Box sx={{ p: 2 }}>
              {tab === 0 && (
                tcr.length === 0 ? (
                  <Typography color="text.secondary">No TCR schedules found.</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Analyte</strong></TableCell>
                          <TableCell><strong>Type</strong></TableCell>
                          <TableCell><strong>Frequency</strong></TableCell>
                          <TableCell><strong>Count</strong></TableCell>
                          <TableCell><strong>Begin Date</strong></TableCell>
                          <TableCell><strong>End Date</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {tcr.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.analyteName} ({row.analyteCode})</TableCell>
                            <TableCell>{row.type}</TableCell>
                            <TableCell>{row.frequency}</TableCell>
                            <TableCell>{row.count}</TableCell>
                            <TableCell>{fmt(row.beginDate)}</TableCell>
                            <TableCell>{fmt(row.endDate)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )
              )}

              {tab === 1 && (
                nonTcrGroup.length === 0 ? (
                  <Typography color="text.secondary">No NonTCR Group schedules found.</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Analyte Group</strong></TableCell>
                          <TableCell><strong>Facility</strong></TableCell>
                          <TableCell><strong>Sample Type</strong></TableCell>
                          <TableCell><strong>Frequency</strong></TableCell>
                          <TableCell><strong>Count</strong></TableCell>
                          <TableCell><strong>Current Range</strong></TableCell>
                          <TableCell><strong>Next Range</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {nonTcrGroup.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.analyteGroupName} ({row.analyteGroupCode})</TableCell>
                            <TableCell>{row.facilityName}</TableCell>
                            <TableCell>{row.sampleType}</TableCell>
                            <TableCell>{row.frequency}</TableCell>
                            <TableCell>{row.sampleCount}</TableCell>
                            <TableCell>{row.rawCurrentMonitoringDateRange}</TableCell>
                            <TableCell>{row.rawNextMonitoringDateRange}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )
              )}

              {tab === 2 && (
                nonTcrIndividual.length === 0 ? (
                  <Typography color="text.secondary">No NonTCR Individual schedules found.</Typography>
                ) : (
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Analyte</strong></TableCell>
                          <TableCell><strong>Facility</strong></TableCell>
                          <TableCell><strong>Sample Type</strong></TableCell>
                          <TableCell><strong>Frequency</strong></TableCell>
                          <TableCell><strong>Count</strong></TableCell>
                          <TableCell><strong>Current Range</strong></TableCell>
                          <TableCell><strong>Next Range</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {nonTcrIndividual.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell>{row.analyteName} ({row.analyteCode})</TableCell>
                            <TableCell>{row.facilityName}</TableCell>
                            <TableCell>{row.sampleType}</TableCell>
                            <TableCell>{row.frequency}</TableCell>
                            <TableCell>{row.sampleCount}</TableCell>
                            <TableCell>{row.rawCurrentMonitoringDateRange}</TableCell>
                            <TableCell>{row.rawNextMonitoringDateRange}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )
              )}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default BottleOrderSchedulesDialog;
